import SettingsFrame from "../../components/Settings";
import ConnectionStatus from "../../components/Settings/ConnectionStatus";
import InvoiceMapping from "../../components/Settings/InvoiceMapping";
import { Content } from "@jobber/components/Content";
import { showToast } from "@jobber/components/Toast";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { getIntegrationStatus, reauthorizeJobber, getInvoiceMappingStrategy, updateInvoiceMappingStrategy, IntegrationStatus } from "../../services";
import { AuthFlowButton, ConnectionState, InvoiceMappingField } from "../../types";
import { showError } from "../../helpers";

const MARKETPLACE_URL = "https://secure.getjobber.com/marketplace";

function Settings() {
    const [status, setStatus] = useState<IntegrationStatus | null>(null);
    const [loadingStatus, setLoadingStatus] = useState(true);

    const [mappingAvailable, setMappingAvailable] = useState(false);
    const [prefix, setPrefix] = useState("");
    const [field, setField] = useState<InvoiceMappingField>(InvoiceMappingField.Id);
    const [savingMapping, setSavingMapping] = useState(false);

    const [searchParams, setSearchParams] = useSearchParams();
    const bootstrapped = useRef(false);

    useEffect(() => {
        // React StrictMode double-invokes effects in dev, and a Jobber auth code
        // is single-use — guard so we only redeem it once
        if (bootstrapped.current) return;
        bootstrapped.current = true;

        (async () => {
            const code = searchParams.get('code');
            if (code) {
                try {
                    const {data: session} = await reauthorizeJobber(code, null);
                    sessionStorage.setItem('state', session.state);
                } catch(e: any) {
                    showError("Error establishing session with Jobber: " + e.message);
                }
                setSearchParams({}, { replace: true });
            }

            const state = sessionStorage.getItem('state');

            let bothConnected = false;
            try {
                const {data} = await getIntegrationStatus(state);
                setStatus(data);
                bothConnected = data.xero === ConnectionState.Connected && data.jobber === ConnectionState.Connected;
            } catch {
                // Status failed → the session is invalid
            } finally {
                setLoadingStatus(false);
            }

            // Invoice mapping requires a working connection on both sides
            // A 403 means the account isn't allow-listed for the feature
            if (bothConnected) {
                try {
                    const {data} = await getInvoiceMappingStrategy(state);
                    setPrefix(data.prefix);
                    setField(data.field);
                    setMappingAvailable(true);
                } catch(e: any) {
                    if (e.response?.status !== 403) {
                        showError("Error loading invoice mapping settings: " + e.message);
                    }
                }
            }
        })();
    }, []);

    const sessionInvalid = !loadingStatus && status === null;

    const saveMapping = async () => {
        const state = sessionStorage.getItem('state');
        setSavingMapping(true);
        try {
            const {data} = await updateInvoiceMappingStrategy({ prefix, field }, state);
            setPrefix(data.prefix);
            setField(data.field);
            showToast({ message: "Settings saved", variation: "success" });
        } catch(e: any) {
            showError("Error saving settings: " + e.message);
        } finally {
            setSavingMapping(false);
        }
    };

    const button: AuthFlowButton = mappingAvailable
        ? {
            link: "",
            text: "Save Settings",
            type: "primary",
            disabled: false,
            loading: savingMapping,
            onClick: saveMapping,
        }
        : { link: MARKETPLACE_URL, text: "Return to Jobber", type: "primary", disabled: false, loading: false };

    return (
        <SettingsFrame
            title={"Settings"}
            button={button}
            loading={loadingStatus}
            loadingMessage={"Loading settings"}
        >
            <Content type={"div"} spacing={"larger"}>
                <ConnectionStatus
                    status={status}
                    sessionInvalid={sessionInvalid}
                    marketplaceUrl={MARKETPLACE_URL}
                />
                {mappingAvailable && (
                    <InvoiceMapping
                        field={field}
                        prefix={prefix}
                        onFieldChange={setField}
                        onPrefixChange={setPrefix}
                    />
                )}
            </Content>
        </SettingsFrame>
    );
}

export default Settings;
